/**
 * spx-nav-card-sticky
 *
 * is-sticky 判断机制：
 *   getBoundingClientRect().top 与期望的 CSS top 值对比，差值 < 容差 → is-sticky
 *   PC 与移动端使用同一机制，各自的胶囊态样式由 CSS 断点控制
 *
 * centerActive：
 *   初始化 / resize 时把 active 项滚动到可视区域中央
 *   滚动容器是 .nav-scroll-area（CSS 中 overflow-x: auto 所在层）
 */
(function () {
  const WRAPPER_SEL = '.shopify-section.spx-nav-card-sticky';
  const NOT_STICKY_CLASS = 'spx-nav-card-sticky--not-sticky';
  const STICKY_CLASS = 'is-sticky';
  const ACTIVE_SEL = '.nav-card-item--active';
  const SCROLL_SEL = '.nav-scroll-area';
  const TOLERANCE = 15;
  const DEBOUNCE_MS = 100;

  const init = () => {
    /** @type {NodeListOf<HTMLElement>} */
    const wrappers = document.querySelectorAll(WRAPPER_SEL);
    wrappers.forEach(setup);
  };

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  /** @param {HTMLElement} wrapper */
  function setup(wrapper) {
    if (wrapper.querySelector('.' + NOT_STICKY_CLASS)) return;
    // Theme Editor 重新渲染时避免重复绑定
    if (wrapper.dataset.spxNavCardStickyInit === 'true') return;
    wrapper.dataset.spxNavCardStickyInit = 'true';

    updateStickyState(wrapper);
    centerActive(wrapper);

    let scrollRaf = 0;
    const onScroll = () => {
      if (scrollRaf) return;
      scrollRaf = requestAnimationFrame(() => {
        scrollRaf = 0;
        updateStickyState(wrapper);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    const onResize = debounce(() => {
      updateStickyState(wrapper);
      centerActive(wrapper);
    }, DEBOUNCE_MS);
    window.addEventListener('resize', onResize);

    // 延迟校验（图片/字体加载后可能影响位置）
    window.addEventListener('load', () => {
      updateStickyState(wrapper);
      centerActive(wrapper);
    }, { once: true });

    // wrapper 被移除时解绑，避免监听器累积
    const observer = new MutationObserver(() => {
      if (wrapper.isConnected) return;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (scrollRaf) cancelAnimationFrame(scrollRaf);
      observer.disconnect();
    });
    if (wrapper.parentNode) {
      observer.observe(wrapper.parentNode, { childList: true });
    }
  }

  /** @param {HTMLElement} wrapper */
  function updateStickyState(wrapper) {
    const rect = wrapper.getBoundingClientRect();
    const expectedTop = parseFloat(getComputedStyle(wrapper).top);
    const isSticky = Math.abs(rect.top - expectedTop) < TOLERANCE;

    if (isSticky !== wrapper.classList.contains(STICKY_CLASS)) {
      wrapper.classList.toggle(STICKY_CLASS, isSticky);
    }
  }

  /** @param {HTMLElement} wrapper */
  function centerActive(wrapper) {
    /** @type {HTMLElement | null} */
    const active = wrapper.querySelector(ACTIVE_SEL);
    if (!active) return;

    /** @type {HTMLElement | null} */
    const scrollEl = wrapper.querySelector(SCROLL_SEL);
    if (!scrollEl) return;

    const cw = scrollEl.clientWidth;
    const sw = scrollEl.scrollWidth;
    if (sw <= cw + 1) return;

    // active 相对滚动容器内容起点的偏移
    const activeRect = active.getBoundingClientRect();
    const scrollRect = scrollEl.getBoundingClientRect();
    const offsetLeft = activeRect.left - scrollRect.left + scrollEl.scrollLeft;

    const target = offsetLeft - cw / 2 + activeRect.width / 2;
    scrollEl.scrollTo({
      left: Math.max(0, Math.min(target, sw - cw)),
      behavior: 'smooth',
    });
  }

  /**
   * @template {(...args: any[]) => void} F
   * @param {F} fn
   * @param {number} wait
   * @returns {(...args: Parameters<F>) => void}
   */
  function debounce(fn, wait) {
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let t;
    return function (...args) {
      if (t) clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  }
})();
